/**
 * Custom Sign-In Page Override
 *
 * Shared sign-in page component used by both the main app and public entry point.
 * Auto-redirects to Microsoft SSO since it's the only auth provider.
 * Renders a simplified INGKA header bar (logo + "Allen") matching
 * the logged-in experience, followed by the built-in SignInPage form.
 */

import { SignInPage } from '@backstage/core-components';
import { type SignInPageProps } from '@backstage/core-plugin-api';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import MuiTypography from '@mui/material/Typography';
import { provider } from '../identityProviders';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import LogoIcon from '../components/Root/LogoIcon';

/**
 * Simplified INGKA-branded header for the sign-in page.
 * Matches the look of the logged-in app bar (navy blue + logo + "Allen").
 */
function SignInHeader() {
  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: '#003E72',
        width: '100%',
        '& *': { fontFamily: 'var(--ikea-font) !important' },
        zIndex: 201,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          height: '72px',
          ml: { xs: 1.5, md: 3 },
          mr: { xs: 1.5, md: 3 },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap="10px"
          sx={{ userSelect: 'none' }}
        >
          <LogoIcon />
          <MuiTypography
            sx={{ fontSize: '2rem', fontWeight: 700, color: '#FFFFFF' }}
          >
            Allen
          </MuiTypography>
        </Box>
      </Toolbar>
    </AppBar>
  );
}

function AllenSignInPage(props: SignInPageProps) {
  return (
    <>
      <SignInHeader />
      <Box sx={{ '& header[class*="BackstageHeader"]': { display: 'none' } }}>
        <SignInPage {...props} provider={provider} auto />
      </Box>
    </>
  );
}

/**
 * Sign-in page module using SignInPageBlueprint
 * Overrides the default sign-in page from appPlugin
 */
export const appModuleSignInPage = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: {
        loader: async () => AllenSignInPage,
      },
    }),
  ],
});
